/**
 * bb-cart-overlay.js
 * ───────────────────────────────────────────────────────────────────────────
 * Full-viewport "Adding items to cart…" curtain, shown from the moment an add
 * starts until the redirect to the cart page lands.
 *
 * WHY. With cart_type: page the theme adds by AJAX and bb-cart-redirect.js then
 * navigates. Between the click and the navigation the shopper is still on a
 * fully interactive product page — they scroll, and the page yanks out from
 * under them a moment later. The add-on modal makes the gap longer still: it
 * closes on confirm, then posts the bundle, then re-reads the cart. This holds
 * the page still and says what is happening.
 *
 * WHERE IT IS TRIGGERED, and why in three places rather than one:
 *   - here, on a WINDOW CAPTURE listener for add-to-cart submits/clicks (see
 *     onAddCommitted for why window, and why capture — and for why it checks
 *     add-on eligibility itself rather than relying on bb-addons.js to
 *     preempt it: bb-addons.js statically imports this module, so THIS
 *     listener is the one registered first, not bb-addons.js's).
 *   - bb-addons.js, at the moment its modal is finalised (confirm or dismiss),
 *     which is the real start of the add for that path.
 *   - bb-cart-redirect.js, as a backstop when a cart add event arrives that
 *     nothing above caught (quick-add on a card, an app, a future surface).
 *
 * THE CURTAIN ONLY EVER COVERS A PENDING REDIRECT. All three triggers above are
 * upstream of one, so the invariant is enforced once, inside showCartOverlay:
 * on the cart page there is no redirect to cover, and every caller is refused.
 * Without that guard, quick-add on the cart page's own recommendation grid
 * raised a curtain that nothing would ever lower — bb-cart-redirect returns
 * early there, so no later code was left to drop it.
 *
 * Every path hides it again on failure — see hideCartOverlay's callers — so a
 * rejected add leaves the shopper on the page with the theme's own error
 * visible rather than stuck behind a curtain that never lifts. The watchdog
 * below is the backstop for anything that neither resolves nor rejects.
 */

import { StandardEvents } from '@shopify/events';

const OVERLAY_ID = 'bb-cart-overlay';
const SCROLL_LOCK_CLASS = 'bb-cart-overlay-open';

/** Add-to-cart controls, matching the selector bb-addons.js intercepts on. */
const ADD_BUTTON_SELECTOR = 'button[name="add"], [ref="addToCartButton"]';

/** Matches bb-addons.js's own DATA_SELECTOR — see isAddonEligible. */
const ADDON_DATA_SELECTOR = 'script[data-bb-addons]';

/**
 * Nothing may hold the curtain up longer than this. It is a backstop, not a
 * timing knob: every normal path drops it explicitly, so this only fires when an
 * add neither resolves nor rejects, or when a surface we never anticipated
 * raises it. A stranded shopper is a far worse outcome than a curtain that
 * lifts a beat early, and a pending redirect unloads the page long before this.
 */
const MAX_CURTAIN_MS = 15000;

/**
 * onAddCommitted raises the curtain on the CLICK/SUBMIT, before product-form.js
 * has even dispatched a CartLinesUpdateEvent — several of its own early-return
 * paths (an already-disabled button, quantity-max validation, an empty batch)
 * never dispatch one at all, and native HTML5 form validation can block the
 * `submit` event from firing in the first place. None of those are an add in
 * progress, so none of them should hold a 15s curtain over an error the
 * shopper cannot see behind it.
 *
 * This is a much shorter, separate timer from MAX_CURTAIN_MS: if no
 * CartLinesUpdateEvent has been observed at all within this window, the add
 * never started and the curtain drops immediately. It does not fire once a
 * real add event is observed — see disarmNoAddTimer — so a genuinely slow add
 * is still covered by the 15s watchdog above, not by this one.
 */
const NO_ADD_OBSERVED_MS = 1500;

let overlay = null;
let watchdog = null;
let noAddTimer = null;

/**
 * Start (or restart) the NO_ADD_OBSERVED_MS countdown. See its doc comment.
 *
 * On fire, this does not simply drop the curtain: product-form.js's
 * handleSubmit has an early exit that queues an add (while a variant change
 * is in flight) WITHOUT dispatching a CartLinesUpdateEvent, so this timer can
 * elapse while an add is genuinely pending, not merely never-started.
 * `data-add-queued` on the component is that signal — see
 * handleSubmit/#drainAddToCartQueue — and we re-arm instead of hiding so a slow
 * variant-change fetch cannot drop the curtain mid-add and reopen the
 * double-add window the queue exists to prevent.
 *
 * Scoped to the form that was actually clicked, NOT a document-wide query: a
 * collection page with quick-add, or the cart page's recommendation rail, has
 * many product-form-components. A document-wide check would let one form's
 * queued add extend another form's curtain to the full 15s watchdog — which is
 * precisely the stranding NO_ADD_OBSERVED_MS exists to prevent.
 *
 * @param {HTMLFormElement} form - the form whose add was committed
 */
function armNoAddTimer(form) {
  const component = form.closest('product-form-component');
  if (noAddTimer !== null) clearTimeout(noAddTimer);
  noAddTimer = setTimeout(() => {
    noAddTimer = null;
    if (component?.hasAttribute('data-add-queued')) {
      armNoAddTimer(form);
      return;
    }
    hideCartOverlay();
  }, NO_ADD_OBSERVED_MS);
}

/** Cancel the countdown above. Safe to call when not armed. */
function disarmNoAddTimer() {
  if (noAddTimer !== null) {
    clearTimeout(noAddTimer);
    noAddTimer = null;
  }
}

/** Cart routes are localised (/fr/cart etc.), so compare against the theme route. */
export function cartUrl() {
  return (typeof Theme !== 'undefined' && Theme?.routes?.cart_url) || '/cart';
}

/**
 * True when the shopper is already on the cart page — where an add redirects
 * nowhere, so the curtain must never go up. Lives here rather than in
 * bb-cart-redirect.js because showCartOverlay is what enforces it; the redirect
 * module imports it back from here, which is the direction that avoids a cycle.
 */
export function alreadyOnCart() {
  if (typeof window === 'undefined') return false;
  const target = cartUrl().replace(/\/+$/, '');
  const here = window.location.pathname.replace(/\/+$/, '');
  return here === target;
}

/**
 * Build the curtain once and reuse it. Created in JS rather than rendered by
 * Liquid so it exists on every template that can add to cart — product pages,
 * collection quick-add, search — without touching five layouts.
 */
function ensureOverlay() {
  if (overlay && overlay.isConnected) return overlay;
  if (typeof document === 'undefined' || !document.body) return null;

  overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.className = 'bb-cart-overlay';
  overlay.hidden = true;

  // role=status + aria-live: announces itself without stealing focus. Not
  // role=alert, which interrupts — this is progress, not a problem.
  overlay.setAttribute('role', 'status');
  overlay.setAttribute('aria-live', 'polite');

  const box = document.createElement('div');
  box.className = 'bb-cart-overlay__box';

  const spinner = document.createElement('div');
  spinner.className = 'bb-cart-overlay__spinner';
  spinner.setAttribute('aria-hidden', 'true');

  const label = document.createElement('p');
  label.className = 'bb-cart-overlay__label';
  label.textContent = 'Adding items to cart…';

  box.append(spinner, label);
  overlay.append(box);
  document.body.appendChild(overlay);
  return overlay;
}

/** True while the curtain is up. */
export function cartOverlayVisible() {
  return Boolean(overlay && !overlay.hidden);
}

/**
 * Raise the curtain and stop the page scrolling behind it.
 *
 * The scroll lock is a class on <html> rather than an inline style so it cannot
 * fight the theme's own drawer/modal locks, which use their own mechanism —
 * whichever releases last simply removes its own class.
 *
 * @returns {boolean} true when the curtain actually went up, false on every
 *   bailout (already on cart, or no document/body to attach to) — callers that
 *   only make sense once the curtain is showing (see onAddCommitted's
 *   armNoAddTimer call) should gate on this rather than assume it always opens.
 */
export function showCartOverlay() {
  // The one invariant, enforced for every caller: no redirect to cover means no
  // curtain. On the cart page bb-cart-redirect.js returns early, so a curtain
  // raised here would have nothing left to lower it.
  if (alreadyOnCart()) return false;

  const el = ensureOverlay();
  if (!el) return false;
  el.hidden = false;
  document.documentElement.classList.add(SCROLL_LOCK_CLASS);

  if (watchdog !== null) clearTimeout(watchdog);
  watchdog = setTimeout(hideCartOverlay, MAX_CURTAIN_MS);
  return true;
}

/** Drop the curtain and release the scroll lock. Safe to call when not shown. */
export function hideCartOverlay() {
  if (watchdog !== null) {
    clearTimeout(watchdog);
    watchdog = null;
  }
  disarmNoAddTimer();
  document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
  if (overlay) overlay.hidden = true;
}

/**
 * True when `form` is a product-form-component whose add-on data (rendered by
 * bb-addons-modal.liquid) matches the currently selected variant — i.e. the add
 * bb-addons.js is about to intercept and hand to its own modal, not a plain add.
 *
 * DUPLICATES bb-addons.js's own readData + matchesFor, deliberately: see
 * onAddCommitted's doc comment for why this listener cannot rely on
 * bb-addons.js to preempt it, and bb-cart-redirect.js's own header comment for
 * why importing FROM bb-addons.js here would create the import cycle both
 * modules are written to avoid. Keep this in sync with bb-addons.js's
 * `matchesFor` — a token is an exact, lowercased string match (§6.2).
 * @param {HTMLFormElement} form
 */
function isAddonEligible(form) {
  const component = form.closest('product-form-component');
  const dataEl = component?.querySelector(ADDON_DATA_SELECTOR);
  if (!dataEl?.textContent) return false;

  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch {
    return false; // malformed data reads as "not eligible" here too — bb-addons.js will reach the same conclusion
  }

  const variantId = String(new FormData(form).get('id') || '');
  const token = (data.variants && data.variants[variantId]) || '';
  if (!token) return false;

  const hasMatch = (list) => (list || []).some((addon) => addon.t === token);
  return hasMatch(data.planting) || hasMatch(data.guarantee);
}

/**
 * Plain (non-add-on) adds: show as soon as the shopper commits, not when the
 * network call resolves.
 *
 * WINDOW CAPTURE. Two earlier attempts failed, and the reasons are worth keeping:
 *
 *   - document BUBBLE: never fired. The theme delegates its add handling from a
 *     document-level capture listener (assets/component.js) and propagation does
 *     not survive it. Measured on an in-stock, non-intercepted product: the add
 *     fired (/cart/add.js requested) while a bubble listener saw no click and no
 *     submit at all.
 *   - document CAPTURE: also never fired. Same node and phase as the theme's own
 *     listener, and listeners there run in REGISTRATION order — component.js is
 *     loaded well before this module, so its handler ran first and stopped the
 *     event before mine was reached.
 *
 * window capture sits one step further out than document capture, so it runs
 * before the theme can stop anything. It ALSO runs before bb-addons.js's own
 * window-capture listener — not after, whatever an earlier version of this
 * comment claimed. bb-addons.js statically imports this module (see its own
 * header comment), and ES module dependencies evaluate before the importing
 * module's body runs, so THIS listener registers first. bb-addons.js's later
 * stopImmediatePropagation() cannot preempt something that already ran.
 * isAddonEligible() above is therefore this listener's own responsibility: it
 * recognises an add-on-eligible, size-matched product and no-ops, or the
 * curtain would cover the modal bb-addons.js is about to open.
 *
 * @param {Event} event
 */
function onAddCommitted(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  /** @type {HTMLFormElement | null} */
  let form;
  if (event.type === 'submit') {
    // Only product forms add to cart; the cart page's own forms update lines.
    form = target instanceof HTMLFormElement ? target : null;
    if (!form || !form.querySelector(ADD_BUTTON_SELECTOR)) return;
  } else {
    const button = target.closest(ADD_BUTTON_SELECTOR);
    if (!button || button.matches('[disabled]')) return;
    // A click that is not inside a form is handled by the submit branch instead.
    form = button.closest('form');
    if (!form) return;
  }

  if (isAddonEligible(form)) return; // bb-addons.js owns this add; a curtain here would cover its modal

  // Only arm the "did anything actually start" timer when the curtain actually
  // went up — showCartOverlay() can bail (already on cart), and a timer whose
  // only job is to lower a curtain that was never raised is pure noise.
  if (!showCartOverlay()) return;
  // The add may never actually start: several product-form.js early-return
  // paths never dispatch a CartLinesUpdateEvent, and native HTML5 validation
  // can block `submit` from firing at all. See NO_ADD_OBSERVED_MS.
  armNoAddTimer(form);
}

if (typeof document !== 'undefined') {
  window.addEventListener('submit', onAddCommitted, true);
  window.addEventListener('click', onAddCommitted, true);

  // A real add was observed — disarm the short "did anything actually start"
  // timer (NO_ADD_OBSERVED_MS) so it cannot yank the curtain down under a
  // genuinely slow add. Listens for ANY CartLinesUpdateEvent rather than
  // filtering by action/context: the only thing that timer guards against is
  // "no event at all after a click", and by the time any such event reaches
  // document, that condition is already false. The 15s watchdog in
  // showCartOverlay remains the backstop for slowness after this point.
  document.addEventListener(StandardEvents.cartLinesUpdate, disarmNoAddTimer);

  // A restored bfcache page must never come back with the curtain up: the
  // shopper pressed Back, so the add is over and the page is interactive again.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) hideCartOverlay();
  });
}
